import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { modamaiTrace } from '../utils/modamaiTrace';
import productReducer from './reducers/productReducer';
import categoryReducer from './reducers/categoryReducer';
import collectionReducer from './reducers/collectionReducer';
import storeSettingsReducer from './reducers/storeSettingsReducer';
import cartReducer from './reducers/cartReducer';
import wishlistReducer from './reducers/wishlistReducer';
import authReducer from './reducers/authReducer';
import reviewsReducer from './reducers/reviewReducer';
import contentReducer from './reducers/contentReducer';
import faqReducer from './reducers/faqReducer';
import orderReducer from './reducers/orderReducer';
import paymentReducer from './reducers/paymentReducer';
import paymentMethodReducer from './reducers/paymentMethodReducer';
import couponReducer from './reducers/couponReducer';
import chatReducer from './reducers/chatReducer';
import { CART_STORAGE_KEY, CART_SESSION_STORAGE_KEY, WISHLIST_STORAGE_KEY } from './constants/storageKeys';

/** Logs product/category Redux actions + REHYDRATE — filter DevTools by [Modamai trace] */
function modamaiReduxTraceMiddleware(_storeApi) {
  return (next) => (action) => {
    const t = action?.type;
    if (typeof t !== 'string') return next(action);

    if (t === REHYDRATE || t.startsWith('persist/')) {
      if (t === REHYDRATE) {
        const keys = action.payload ? Object.keys(action.payload) : [];
        modamaiTrace('redux persist/REHYDRATE inbound keys', keys);
        const prod = action.payload?.product;
        if (prod) {
          modamaiTrace('redux REHYDRATE includes product slice', {
            shopCatalogLen: prod.shopCatalogList?.length,
            homeRailLen: prod.homeRail?.length,
          });
        }
      }
      return next(action);
    }

    if (t.includes('product/') || t.includes('category/') || t.includes('collection/')) {
      let payload = action.payload;
      if (payload && typeof payload === 'object') {
        const p = payload;
        if ('items' in p && Array.isArray(p.items)) {
          payload = { ...p, items: `[${p.items.length} items]` };
        }
        if ('rail' in p && Array.isArray(p.rail)) {
          payload = {
            ...p,
            rail: `[${p.rail.length}]`,
            grid: Array.isArray(p.grid) ? `[${p.grid.length}]` : p.grid,
          };
        }
      }
      modamaiTrace(`redux dispatch → ${t}`, payload);
    }
    return next(action);
  };
}

const rootReducer = combineReducers({
  product: productReducer,
  category: categoryReducer,
  collection: collectionReducer,
  storeSettings: storeSettingsReducer,
  cart: cartReducer,
  wishlist: wishlistReducer,
  auth: authReducer,
  reviews: reviewsReducer,
  content: contentReducer,
  faq: faqReducer,
  order: orderReducer,
  payment: paymentReducer,
  paymentMethod: paymentMethodReducer,
  coupon: couponReducer,
  chat: chatReducer,
});

const persistKeySuffix = (process.env.REACT_APP_STORE_ID || 'host').replace(/[^a-zA-Z0-9_-]/g, '');

const persistConfig = {
  key: `modamai-cache-${persistKeySuffix}`,
  version: 4,
  storage,
  /**
   * Do not persist `product` — stale/empty shop lists were rehydrating and hiding fresh API data.
   * Categories + store settings cache only. Cart & wishlist sync via subscribe + legacy keys.
   */
  whitelist: ['category', 'storeSettings', 'content', 'faq'],
  throttle: 1000,
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        warnAfter: 128,
      },
      immutableCheck: false,
    }).concat(modamaiReduxTraceMiddleware),
});

let lastProductSliceSnap = '';

/**
 * Deduped `[modamai-debug] redux product snapshot` — see subscribe + persist callback.
 */
function maybeLogProductSnapshot(getStateFn) {
  try {
    const p = getStateFn()?.product;
    if (!p) return;
    const snap = JSON.stringify({
      sc: Array.isArray(p.shopCatalogList) ? p.shopCatalogList.length : -1,
      hl: Array.isArray(p.homeRail) ? p.homeRail.length : -1,
      hg: Array.isArray(p.homeGrid) ? p.homeGrid.length : -1,
      scl: Boolean(p.shopCatalogLoading),
      hml: Boolean(p.homeLoading),
      dl: Boolean(p.detailLoading),
      ds: p.detail?.slug ?? null,
      sce: Boolean(p.shopCatalogError),
      he: Boolean(p.homeError),
      de: Boolean(p.detailError),
    });
    if (snap === lastProductSliceSnap) return;
    lastProductSliceSnap = snap;
    modamaiTrace('redux product slice snapshot (changed)', {
      shopCatalogListLen: p.shopCatalogList?.length ?? 'n/a',
      homeRailLen: p.homeRail?.length ?? 'n/a',
      homeGridLen: p.homeGrid?.length ?? 'n/a',
      shopCatalogLoading: p.shopCatalogLoading,
      homeLoading: p.homeLoading,
      detailLoading: p.detailLoading,
      detailSlug: p.detail?.slug,
      errors: {
        shopCatalogError: p.shopCatalogError ?? null,
        homeError: p.homeError ?? null,
        detailError: p.detailError ?? null,
      },
    });
  } catch {
    /* ignore */
  }
}

const persistor = persistStore(store, undefined, () => {
  maybeLogProductSnapshot(() => store.getState());
  modamaiTrace('persistStore rehydration callback fired', {
    shopCatalogLen: store.getState().product?.shopCatalogList?.length ?? 0,
    categoryRootsLen: Array.isArray(store.getState().category?.roots)
      ? store.getState().category.roots.length
      : 0,
  });
});

store.subscribe(() => {
  try {
    maybeLogProductSnapshot(() => store.getState());
    const { cart, wishlist } = store.getState();
    if (Array.isArray(cart?.items)) {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart.items));
    }
    if (cart?.cartSessionId) {
      localStorage.setItem(CART_SESSION_STORAGE_KEY, String(cart.cartSessionId));
    }
    if (Array.isArray(wishlist?.ids)) {
      localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(wishlist.ids));
    }
  } catch {
    /* ignore */
  }
});

export { store, persistor };
